// /account — passwordless license portal. Email → 6-digit code (Resend) →
// session cookie → the licenses dashboard: download + getting-started +
// billing cards on the left, license cards (key, badge, valid-for, status,
// expiry, trial usage) on the right. No passwords anywhere.
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { CRWIcon, type IconName } from '../components/icons';
import { MetalBtn, MonoNote } from '../components/primitives';
import { BigFooter, DISCORD_INVITE } from '../sections/footer';
import { PageShell, usePageTitle } from './shell';
import { hashLicenseKey, phCapture, phIdentify, phSetPerson } from '../posthog';

interface Purchase {
  plan: string; status: string; licenseKey: string | null; createdAt: string; subscription: boolean;
  usage?: { uses: number; maxUses: number } | null;
}

const PLAN_BADGE: Record<string, string> = {
  trial: 'Free trial',
  monthly: 'Monthly',
  annual: 'Annual',
  founding: 'Founding Edition',
};

const UPDATES = 'https://updates.getclipradar.com';
// The panel file carries no version in its name, so this URL cannot go stale.
// The installer URL deliberately has NO hardcoded fallback: R2 keeps only the
// newest 3 releases, so a pinned version 404s within three ships. Until
// /api/latest answers, the button routes to /download instead of guessing.
const CCX = `${UPDATES}/co.clipradar.premiere_premierepro.ccx`;

const inputStyle = {
  width: '100%', boxSizing: 'border-box' as const, fontFamily: 'var(--font-mono)', fontSize: 14, color: 'var(--fg-1)',
  background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.14)', borderRadius: 10,
  padding: '13px 14px', boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.4)',
};

// left-column action button — neutral glass, hover/press live in .crw-acct-btn
const cardBtn = {
  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, width: '100%', boxSizing: 'border-box' as const,
  padding: '11px 16px', borderRadius: 10, fontSize: 13, fontWeight: 600, color: 'var(--fg-1)', textDecoration: 'none',
  background: 'rgba(255,255,255,0.05)', border: '1px solid var(--line-2)', cursor: 'pointer',
  fontFamily: 'var(--font-body)', transition: 'background 150ms var(--ease-out)',
};

// tertiary link row for the "getting started" card — quiet label + trailing
// arrow so docs/Discord read below the download primary (ui-critic)
function GsLink({ to, href, children, divider }: { to?: string; href?: string; children: React.ReactNode; divider?: boolean }) {
  const style: React.CSSProperties = {
    display: 'flex', alignItems: 'center', gap: 10, width: '100%', boxSizing: 'border-box',
    padding: '9px 2px', fontSize: 13, fontWeight: 500, color: 'var(--fg-2)', textDecoration: 'none',
    borderBottom: divider ? '1px solid var(--line-1)' : 'none', borderRadius: 0,
  };
  const inner = (
    <>
      <span style={{ flex: 1, minWidth: 0 }}>{children}</span>
      <CRWIcon d="arrowRight" size={13} style={{ opacity: 0.45, flex: 'none' }} />
    </>
  );
  return href
    ? <a href={href} target="_blank" rel="noopener noreferrer" className="crw-ghostbtn" style={style}>{inner}</a>
    : <Link to={to!} className="crw-ghostbtn" style={style}>{inner}</Link>;
}

// mono micro-label + signal dot — the site's wayfinding tick
function ColLabel({ children }: { children: React.ReactNode }) {
  return (
    <span className="cr-label" style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--signal-400)', marginBottom: 2 }}>
      <span aria-hidden="true" style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--signal-400)', boxShadow: '0 0 8px var(--signal-glow)' }}></span>
      {children}
    </span>
  );
}

function SideCard({ icon, title, sub, children }: { icon: IconName; title: string; sub: string; children: React.ReactNode }) {
  return (
    <div className="crw-glass" style={{ borderRadius: 16, padding: '18px 18px 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <span aria-hidden="true" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 34, height: 34, borderRadius: 10, flex: 'none', color: 'var(--fg-2)', background: 'rgba(255,255,255,0.05)', border: '1px solid var(--line-2)' }}>
          <CRWIcon d={icon} size={16} />
        </span>
        <span style={{ minWidth: 0 }}>
          <b style={{ display: 'block', fontSize: 14.5, fontWeight: 650, color: 'var(--fg-1)' }}>{title}</b>
          <i style={{ display: 'block', fontStyle: 'normal', fontSize: 12.5, lineHeight: 1.55, color: 'var(--fg-3)', marginTop: 3 }}>{sub}</i>
        </span>
      </div>
      {children}
    </div>
  );
}

// The plan chip — Founding is the one lit tier (warmth law); the rest read as
// quiet neutral chips, differentiated by their content, not their hue.
function PlanBadge({ plan }: { plan: string }) {
  const founding = plan === 'founding';
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      fontFamily: 'var(--font-mono)', fontSize: 9.5, letterSpacing: '0.1em', textTransform: 'uppercase',
      padding: '3px 9px', borderRadius: 999,
      color: founding ? 'var(--signal-300)' : 'var(--fg-2)',
      background: founding ? 'var(--signal-dim)' : 'rgba(255,255,255,0.05)',
      border: `1px solid ${founding ? 'rgba(var(--light-mid-rgb),0.32)' : 'var(--line-2)'}`,
    }}>
      {founding ? <CRWIcon d="sparkle" size={10} /> : null}{PLAN_BADGE[plan] ?? plan}
    </span>
  );
}

// "Valid for" marks — the desktop app + the Premiere panel. The desktop tile is
// glacier-lit; the Pr tile keeps Premiere's own brand purple (honest external mark).
function ValidFor() {
  return (
    <span style={{ display: 'inline-flex', gap: 6 }}>
      <span title="ClipRadar desktop app" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 26, height: 26, borderRadius: 7, color: 'var(--signal-300)', background: 'var(--signal-dim)', border: '1px solid rgba(var(--light-mid-rgb),0.3)' }}>
        <CRWIcon d="monitor" size={13} />
      </span>
      <span title="Premiere Pro panel" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 26, height: 26, borderRadius: 7, fontFamily: 'var(--font-display)', fontSize: 10.5, fontWeight: 700, color: '#EA77FF', background: '#1E0B2E', border: '1px solid rgba(234,119,255,0.35)' }}>
        Pr
      </span>
    </span>
  );
}

export function AccountPage() {
  usePageTitle('Your account');
  const [phase, setPhase] = useState<'loading' | 'email' | 'code' | 'account'>('loading');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [account, setAccount] = useState<{ email: string; purchases: Purchase[] } | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [dl, setDl] = useState<{ exe: string; ccx: string } | null>(null);
  const codeRef = useRef<HTMLInputElement | null>(null);

  const loadAccount = async (): Promise<boolean> => {
    try {
      const r = await fetch('/api/account');
      if (!r.ok) return false;
      const j = await r.json();
      // anonymous visitors get 200 {signedIn:false} (no `email`) — that's logged-out
      if (!j || typeof j.email !== 'string') return false;
      setAccount(j);
      setPhase('account');
      phIdentify(j.email); // signed-in = named person; cookie journey merges in
      // Analytics: the portal is where the license key is visible client-side,
      // so this is where the website side of the purchase↔app join happens —
      // the SAME sha256[:12] hash the app sends on activation. Never the key.
      const firstKey = (j.purchases ?? []).find((p: { licenseKey: string | null }) => p.licenseKey)?.licenseKey;
      if (firstKey) {
        void hashLicenseKey(firstKey).then((h) => {
          if (h) phSetPerson({ license_key_hash: h });
        });
      }
      phCapture('account_signed_in', { purchase_count: (j.purchases ?? []).length });
      return true;
    } catch { return false; }
  };

  // an existing session cookie skips the login entirely
  useEffect(() => {
    void (async () => { if (!(await loadAccount())) setPhase('email'); })();
  }, []);

  // live download links for the left column — the hardcoded fallback above is a
  // dead pruned build, so we ALWAYS resolve the current installer via /api/latest
  // and only keep the fallback if that fetch fails (matches /download).
  useEffect(() => {
    let live = true;
    fetch('/api/latest')
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (live && d && typeof d.exe === 'string') setDl({ exe: d.exe, ccx: typeof d.ccx === 'string' ? d.ccx : CCX });
      })
      .catch(() => {});
    return () => { live = false; };
  }, []);

  const requestCode = async () => {
    if (busy) return;
    setError(null); setBusy(true);
    try {
      const r = await fetch('/api/auth/request-code', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email: email.trim() }) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setError(d.error || 'That didn’t work — try again.'); return; }
      setCode('');
      setPhase('code');
      window.setTimeout(() => codeRef.current?.focus(), 30);
    } catch { setError('That didn’t work — try again.'); } finally { setBusy(false); }
  };

  const verify = async (value?: string) => {
    const c = (value ?? code).trim();
    if (busy || c.length !== 6) return;
    setError(null); setBusy(true);
    try {
      const r = await fetch('/api/auth/verify', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email: email.trim(), code: c }) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setError(d.error || 'Wrong code.'); return; }
      await loadAccount();
    } catch { setError('That didn’t work — try again.'); } finally { setBusy(false); }
  };

  const openPortal = async () => {
    if (busy) return;
    setBusy(true); setError(null);
    try {
      const r = await fetch('/api/account/portal', { method: 'POST' });
      const d = await r.json().catch(() => ({}));
      if (r.ok && d.url) { window.location.assign(d.url); return; }
      setError(d.error || 'Could not open the billing portal.');
    } catch { setError('Could not open the billing portal.'); } finally { setBusy(false); }
  };

  const logout = async () => {
    await fetch('/api/auth/logout', { method: 'POST' }).catch(() => {});
    setAccount(null); setEmail(''); setCode(''); setPhase('email');
  };

  const copyKey = async (key: string) => {
    try { await navigator.clipboard.writeText(key); setCopied(key); window.setTimeout(() => setCopied(null), 1600); } catch { /* clipboard denied */ }
  };

  const errorLine = error ? (
    <p role="alert" style={{ margin: '12px 0 0', fontFamily: 'var(--font-mono)', fontSize: 11.5, letterSpacing: '0.03em', color: '#f87171' }}>{error}</p>
  ) : null;

  /* ---------- signed OUT: the passwordless login flow ---------- */
  if (phase !== 'account') {
    return (
      <PageShell kicker="Your account" title="Sign in to ClipRadar."
        sub="No passwords. Enter the email you started your trial or bought with, and we’ll send you a 6-digit code.">
        <div style={{ maxWidth: 480 }}>
          {phase === 'loading' ? (
            <div className="crw-glass" style={{ borderRadius: 16, padding: '22px 22px', display: 'flex', alignItems: 'center', gap: 12 }}>
              <span aria-hidden="true" className="crw-pulse" style={{ width: 9, height: 9, borderRadius: '50%', flex: 'none', background: 'var(--signal-400)', boxShadow: '0 0 12px var(--signal-glow)' }}></span>
              <MonoNote>Checking your session…</MonoNote>
            </div>
          ) : phase === 'email' ? (
            <div className="crw-glass crw-gradborder" style={{ position: 'relative', overflow: 'hidden', borderRadius: 16, padding: '24px 22px' }}>
              <span aria-hidden="true" style={{ position: 'absolute', top: 0, left: '6%', right: '6%', height: 1, background: 'linear-gradient(90deg, transparent, rgba(var(--light-high-rgb),0.6), rgba(var(--light-mid-rgb),0.45), transparent)' }}></span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                <span aria-hidden="true" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 30, height: 30, borderRadius: 9, flex: 'none', color: 'var(--signal-300)', background: 'var(--signal-dim)', border: '1px solid rgba(var(--light-mid-rgb),0.3)' }}>
                  <CRWIcon d="at" size={15} />
                </span>
                <label htmlFor="acct-email" style={{ fontSize: 13, fontWeight: 600, color: 'var(--fg-1)' }}>Email address</label>
              </div>
              <input id="acct-email" type="email" value={email} placeholder="you@studio.com" autoComplete="email"
                onChange={(e) => { setEmail(e.target.value); setError(null); }}
                onKeyDown={(e) => { if (e.key === 'Enter') void requestCode(); }}
                className="crw-input" style={inputStyle} />
              <div style={{ marginTop: 14 }}>
                <MetalBtn onClick={() => void requestCode()} busy={busy} style={{ width: '100%' }}>{busy ? 'Sending…' : 'Email me a sign-in code'}</MetalBtn>
              </div>
              {errorLine}
            </div>
          ) : (
            <div className="crw-glass crw-gradborder" style={{ position: 'relative', overflow: 'hidden', borderRadius: 16, padding: '24px 22px' }}>
              <span aria-hidden="true" style={{ position: 'absolute', top: 0, left: '6%', right: '6%', height: 1, background: 'linear-gradient(90deg, transparent, rgba(var(--light-high-rgb),0.6), rgba(var(--light-mid-rgb),0.45), transparent)' }}></span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                <span aria-hidden="true" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 30, height: 30, borderRadius: 9, flex: 'none', color: 'var(--signal-300)', background: 'var(--signal-dim)', border: '1px solid rgba(var(--light-mid-rgb),0.3)' }}>
                  <CRWIcon d="lock" size={14} />
                </span>
                <label htmlFor="acct-code" style={{ fontSize: 13, fontWeight: 600, color: 'var(--fg-1)' }}>Enter the 6-digit code</label>
              </div>
              <p style={{ margin: '0 0 12px', fontSize: 12.5, color: 'var(--fg-3)' }}>Sent to <b style={{ color: 'var(--fg-2)' }}>{email.trim()}</b> — it expires in 10 minutes.</p>
              <input id="acct-code" ref={codeRef} inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code}
                placeholder="••••••"
                onChange={(e) => {
                  const v = e.target.value.replace(/\D/g, '').slice(0, 6);
                  setCode(v); setError(null);
                  if (v.length === 6) void verify(v);
                }}
                onKeyDown={(e) => { if (e.key === 'Enter') void verify(); }}
                className="crw-input" style={{ ...inputStyle, fontSize: 22, letterSpacing: '0.5em', textAlign: 'center' }} />
              <div style={{ marginTop: 14 }}>
                <MetalBtn onClick={() => void verify()} busy={busy} style={{ width: '100%' }}>{busy ? 'Checking…' : 'Sign in'}</MetalBtn>
              </div>
              {errorLine}
              <div style={{ display: 'flex', gap: 16, marginTop: 14 }}>
                <button onClick={() => void requestCode()} className="crw-linkbtn" style={{ fontFamily: 'var(--font-mono)', fontSize: 11.5, color: 'var(--signal-400)' }}>Resend code</button>
                <button onClick={() => { setPhase('email'); setError(null); }} className="crw-linkbtn" style={{ fontFamily: 'var(--font-mono)', fontSize: 11.5, color: 'var(--fg-3)' }}>Different email</button>
              </div>
            </div>
          )}
        </div>
      </PageShell>
    );
  }

  /* ---------- signed IN: the licenses dashboard ---------- */
  const purchases = account?.purchases ?? [];
  const hasSubscription = purchases.some((p) => p.subscription);
  const hasPaid = purchases.some((p) => p.plan !== 'trial' && p.status === 'licensed');
  // A spent trial card already shows its own "Upgrade" banner — suppress the
  // standalone buy button then, so each state has exactly one purchase CTA
  // (mid-trial keeps the standalone button as its only path).
  const anySpent = purchases.some((p) => !!(p.usage && p.usage.maxUses > 0 && p.usage.uses >= p.usage.maxUses));
  const count = purchases.length;

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <main style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
        {/* atmosphere — the room, one glacier light top-centre (no radar, no violet) */}
        <div aria-hidden="true" style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
          <div style={{ position: 'absolute', inset: 0, background: 'var(--bg-grid)', maskImage: 'linear-gradient(180deg, black, transparent 460px)', WebkitMaskImage: 'linear-gradient(180deg, black, transparent 460px)' }}></div>
          <div style={{ position: 'absolute', top: -280, left: '50%', transform: 'translateX(-50%)', width: 940, height: 540, background: 'radial-gradient(ellipse at center, rgba(var(--light-rgb),0.10), rgba(var(--light-deep-rgb),0.05) 50%, transparent 72%)', filter: 'blur(34px)' }}></div>
        </div>

        {/* top bar — Back + email + Log out */}
        <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 12, padding: '18px 22px' }}>
          <Link to="/" className="crw-glass" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '9px 16px 9px 12px', borderRadius: 11, textDecoration: 'none', fontSize: 13, fontWeight: 500, color: 'var(--fg-1)' }}>
            <CRWIcon d="arrowRight" size={13} style={{ transform: 'rotate(180deg)' }} /> Back
          </Link>
          <span style={{ flex: 1 }}></span>
          <span className="crw-acct-email" style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            <MonoNote style={{ textTransform: 'none', letterSpacing: '0.03em' }}>{account?.email}</MonoNote>
          </span>
          <button onClick={() => void logout()} className="crw-glass" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '9px 14px', borderRadius: 11, fontSize: 13, fontWeight: 500, color: 'var(--fg-1)', cursor: 'pointer', fontFamily: 'var(--font-body)' }}>
            Log out <CRWIcon d="toLine" size={13} />
          </button>
        </div>

        <div className="crw-page-in" style={{ position: 'relative', width: '100%', maxWidth: 1140, margin: '0 auto', boxSizing: 'border-box', padding: '20px 22px 90px' }}>
          {/* page header */}
          <header style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 30 }}>
            <ColLabel>License portal</ColLabel>
            <h1 className="crw-h-grad" style={{ margin: 0, fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 'clamp(28px, 4vw, 44px)', letterSpacing: 'var(--tracking-display)', lineHeight: 1.08 }}>
              Your account
            </h1>
            <p style={{ margin: 0, fontSize: 15, lineHeight: 1.65, color: 'var(--fg-2)' }}>
              {count === 0
                ? <>No licenses on <b style={{ color: 'var(--fg-1)', overflowWrap: 'anywhere' }}>{account?.email}</b> yet.</>
                : <>{count} {count === 1 ? 'license' : 'licenses'} on <b style={{ color: 'var(--fg-1)', overflowWrap: 'anywhere' }}>{account?.email}</b>.</>}
            </p>
          </header>

          <div className="crw-acct-grid" style={{ display: 'grid', gridTemplateColumns: '320px 1fr', gap: 20, alignItems: 'start' }}>

            {/* ---- left column: get set up ---- */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <ColLabel>Get started</ColLabel>
              <SideCard icon="download" title="Download ClipRadar" sub="The app for Windows — and the optional Premiere Pro panel.">
                {/* the download is the primary action of this column — a lit silver
                    instrument (neutral metal, no warm chroma) over the dark secondary */}
                {dl ? (
                  <a href={dl.exe} download style={{ textDecoration: 'none', display: 'flex' }}>
                    <MetalBtn neutral style={{ width: '100%' }}><CRWIcon d="download" size={14} />Download for Windows</MetalBtn>
                  </a>
                ) : (
                  /* version not resolved yet — send them to /download, which owns
                     the loading and unavailable states, rather than guess a URL */
                  <Link to="/download" style={{ textDecoration: 'none', display: 'flex' }}>
                    <MetalBtn neutral style={{ width: '100%' }}><CRWIcon d="download" size={14} />Download for Windows</MetalBtn>
                  </Link>
                )}
                <a href={dl?.ccx ?? CCX} download className="crw-acct-btn" style={cardBtn}><CRWIcon d="download" size={14} /> Premiere panel (.ccx)</a>
              </SideCard>

              <SideCard icon="help" title="Getting started" sub="How to install ClipRadar and activate your license key.">
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <GsLink to="/docs" divider>Quick start guide</GsLink>
                  <GsLink to="/docs" divider>Read the documentation</GsLink>
                  <GsLink href={DISCORD_INVITE}>Join our Discord community</GsLink>
                </div>
              </SideCard>

              {hasSubscription ? (
                <SideCard icon="tag" title="Manage billing" sub="Your subscription, payment method and invoices — via Stripe.">
                  <button onClick={() => void openPortal()} className="crw-acct-btn" style={cardBtn}>{busy ? 'Opening…' : 'Open billing portal'}</button>
                </SideCard>
              ) : null}
            </div>

            {/* ---- right column: the licenses ---- */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <ColLabel>Your licenses</ColLabel>

              {purchases.length === 0 ? (
                <div className="crw-glass" style={{ borderRadius: 16, padding: '24px 22px' }}>
                  <b style={{ fontSize: 15, color: 'var(--fg-1)' }}>No licenses on this email yet.</b>
                  <p style={{ margin: '8px 0 0', fontSize: 13.5, lineHeight: 1.65, color: 'var(--fg-2)' }}>
                    Start a <Link to="/trial" style={{ color: 'var(--signal-400)', textDecoration: 'none' }}>free trial</Link> or buy a plan at{' '}
                    <Link to={{ pathname: '/', hash: '#pricing' }} style={{ color: 'var(--signal-400)', textDecoration: 'none' }}>pricing</Link> — bought with a
                    different email? Log out and try that one.
                  </p>
                </div>
              ) : purchases.map((p, i) => {
                // A trial with every search spent must NOT read as a healthy "Active"
                // license — it gets a distinct used-up treatment + an upgrade path.
                const spent = !!(p.usage && p.usage.maxUses > 0 && p.usage.uses >= p.usage.maxUses);
                return (
                <div key={i} className="crw-glass crw-gradborder crw-fade" style={{ animationDelay: `${i * 70}ms`, position: 'relative', overflow: 'hidden', borderRadius: 16, padding: '20px 22px', display: 'flex', flexDirection: 'column', gap: 14 }}>
                  {/* hairline — glacier for a live license, amber for a spent one */}
                  <span aria-hidden="true" style={{ position: 'absolute', top: 0, left: '6%', right: '6%', height: 1, background: spent ? 'linear-gradient(90deg, transparent, rgba(255,196,77,0.7), transparent)' : 'linear-gradient(90deg, transparent, rgba(var(--light-high-rgb),0.6), rgba(var(--light-mid-rgb),0.45), transparent)' }}></span>
                  {/* header: product + plan badge + trial usage */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <b style={{ fontSize: 15.5, fontWeight: 650, color: 'var(--fg-1)' }}>ClipRadar</b>
                    <PlanBadge plan={p.plan} />
                    {spent ? (
                      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 9, letterSpacing: '0.1em', textTransform: 'uppercase', padding: '3px 9px', borderRadius: 999, color: 'var(--warning)', background: 'var(--warning-dim)', border: '1px solid rgba(255,196,77,0.3)' }}>
                        Used up
                      </span>
                    ) : null}
                    <span style={{ flex: 1 }}></span>
                    {p.usage ? (
                      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11.5, color: 'var(--fg-3)' }}>
                        <b style={{ color: spent ? 'var(--warning)' : 'var(--fg-1)', fontSize: 14 }}>{p.usage.uses}</b> / {p.usage.maxUses} searches used
                      </span>
                    ) : null}
                  </div>
                  {/* trial usage meter — amber once spent */}
                  {p.usage ? (
                    <div aria-hidden="true" style={{ position: 'relative', height: 4, borderRadius: 999, background: 'rgba(255,255,255,0.07)', overflow: 'hidden' }}>
                      <span style={{ position: 'absolute', top: 0, bottom: 0, left: 0, width: `${Math.min(100, Math.round((p.usage.uses / Math.max(1, p.usage.maxUses)) * 100))}%`, minWidth: p.usage.uses > 0 ? 6 : 0, borderRadius: 999, background: spent ? 'var(--warning)' : 'linear-gradient(90deg, var(--signal-300), var(--signal-500))', boxShadow: spent ? '0 0 10px rgba(255,196,77,0.4)' : '0 0 10px var(--signal-glow)' }}></span>
                    </div>
                  ) : null}

                  {/* the key */}
                  {p.licenseKey ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <MonoNote style={{ fontSize: 9.5 }}>License key</MonoNote>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <code style={{ flex: 1, minWidth: 0, fontFamily: 'var(--font-mono)', fontSize: 12.5, color: 'var(--fg-1)', background: 'rgba(0,0,0,0.35)', border: '1px solid var(--line-2)', borderRadius: 9, padding: '10px 12px', overflowWrap: 'anywhere' }}>{p.licenseKey}</code>
                        <button onClick={() => void copyKey(p.licenseKey!)} aria-label="Copy license key"
                          className="crw-copybtn" style={{ color: copied === p.licenseKey ? 'var(--positive)' : 'var(--fg-1)' }}>
                          <CRWIcon d={copied === p.licenseKey ? 'check' : 'layers'} size={12} />{copied === p.licenseKey ? 'Copied' : 'Copy'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <p style={{ margin: 0, fontSize: 13, color: 'var(--fg-3)' }}>Your key is being generated — it’ll appear here (and in your inbox) within minutes.</p>
                  )}

                  {/* valid-for / status / expires */}
                  <div className="crw-acct-facts" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
                    <div style={{ borderRadius: 10, border: '1px solid var(--line-1)', background: 'rgba(255,255,255,0.02)', padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 7 }}>
                      <MonoNote style={{ fontSize: 9 }}>Valid for</MonoNote>
                      <ValidFor />
                    </div>
                    <div style={{ borderRadius: 10, border: '1px solid var(--line-1)', background: 'rgba(255,255,255,0.02)', padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 7 }}>
                      <MonoNote style={{ fontSize: 9 }}>Status</MonoNote>
                      <b style={{ fontSize: 13.5, fontWeight: 600, color: spent ? 'var(--warning)' : p.status === 'licensed' ? 'var(--positive)' : p.status === 'pending' ? 'var(--warning)' : 'var(--danger)' }}>
                        {spent ? 'All searches used' : p.status === 'licensed' ? 'Active' : p.status === 'pending' ? 'Pending' : 'Suspended'}
                      </b>
                    </div>
                    <div style={{ borderRadius: 10, border: '1px solid var(--line-1)', background: 'rgba(255,255,255,0.02)', padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 7 }}>
                      <MonoNote style={{ fontSize: 9 }}>Expires</MonoNote>
                      <b style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--fg-1)' }}>{p.subscription ? 'While subscribed' : 'Never'}</b>
                    </div>
                  </div>

                  {/* spent trial: the way forward, right on the card */}
                  {spent ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', borderRadius: 10, border: '1px solid rgba(255,196,77,0.25)', background: 'var(--warning-dim)', padding: '12px 14px' }}>
                      <span style={{ flex: 1, minWidth: 220, fontSize: 12.5, lineHeight: 1.55, color: 'var(--fg-2)' }}>
                        You've used all {p.usage!.maxUses} trial searches. Your library and index stay
                        intact — upgrade to keep searching without limits.
                      </span>
                      <Link to={{ pathname: '/', hash: '#pricing' }} style={{ textDecoration: 'none', display: 'inline-flex' }}>
                        <MetalBtn size="sm">Upgrade — go unlimited</MetalBtn>
                      </Link>
                    </div>
                  ) : null}
                </div>
              );})}

              {!hasPaid && !anySpent ? (
                <Link to={{ pathname: '/', hash: '#pricing' }} className="crw-acct-btn" style={{ ...cardBtn, padding: '13px 16px' }}>
                  Buy a ClipRadar license
                </Link>
              ) : null}
              {errorLine}
            </div>
          </div>
        </div>
      </main>
      <BigFooter />
    </div>
  );
}
